import * as z from "zod";
import { RepeatMode } from "../../forms/export/dateUtils";

export enum SelectionType {
  Inclusive = "Inclusive",
  Exclusive = "Exclusive",
}

const SchoolFormIdSchema = z.object({
  formId: z.string().uuid(),
  schoolId: z.string().uuid().nullable(),
  formSchoolRankId: z.string().uuid().nullable(),
});

const SelectionSchema = z.object({
  selectionType: z.nativeEnum(SelectionType),
  ids: z.array(SchoolFormIdSchema),
});
export type SelectionObject = z.infer<typeof SelectionSchema>;

const ExportConfigWithoutOrgIdSchema = z.object({
  formTemplateId: z.string().uuid(),
  searchParamsString: z.string(),
  selection: SelectionSchema,
  selectedQuestionIds: z.array(z.string().uuid()).optional(),
  selectedColumns: z.array(z.string()).optional(),
  questionKeysAsHeaders: z.boolean().default(false), // export question keys, rather than labels, as column headers
  exportSelectAnswerValues: z.boolean().default(false), // export answer value, rather than label, for select-type questions
});
export type ExportConfigWithoutOrgId = z.infer<
  typeof ExportConfigWithoutOrgIdSchema
>;

const ExportConfigSchema = ExportConfigWithoutOrgIdSchema.extend({
  organizationId: z.string().uuid(),
});
export type ExportConfig = z.infer<typeof ExportConfigSchema>;

const ScheduleConfigSchema = z.object({
  startTime: z.string().datetime(),
  repeatMode: z.nativeEnum(RepeatMode).nullable(),
  timeZone: z.string().optional(),
});
export type ScheduleConfig = z.infer<typeof ScheduleConfigSchema>;

export enum SFTPAuthMethod {
  Password = "Password",
  SSHKey = "SSH Key",
}

export const SFTPConfigSchema = z.object({
  host: z.string(),
  port: z.number(),
  username: z.string(),
  authMethod: z.nativeEnum(SFTPAuthMethod),
  secret: z.string(),
  path: z.string(),
});

export enum GoogleDriveAuthMethodType {
  ServiceAccount = "Service Account",
}
export enum GoogleDriveConfigType {
  UpdateSheet = "update-sheet",
  CreateSheet = "create-sheet",
  UploadCSV = "upload-csv",
}

const GoogleDriveConfigBaseSchema = z.object({
  authMethod: z.nativeEnum(GoogleDriveAuthMethodType),
});

const GoogleSheetsUpdateSchema = GoogleDriveConfigBaseSchema.extend({
  type: z.literal(GoogleDriveConfigType.UpdateSheet),
  spreadsheetId: z.string(),
  sheetName: z.string().optional(),
});
export type GoogleSheetsUpdateConfig = z.infer<typeof GoogleSheetsUpdateSchema>;

const GoogleSheetsCreateSchema = GoogleDriveConfigBaseSchema.extend({
  type: z.literal(GoogleDriveConfigType.CreateSheet),
  folderId: z.string(),
  sheetName: z.string().optional(),
});
export type GoogleSheetsCreateConfig = z.infer<typeof GoogleSheetsCreateSchema>;

const GoogleDriveCSVUploadSchema = GoogleDriveConfigBaseSchema.extend({
  type: z.literal(GoogleDriveConfigType.UploadCSV),
  folderId: z.string(),
});
export type GoogleDriveCSVUploadConfig = z.infer<
  typeof GoogleDriveCSVUploadSchema
>;

export const GoogleDriveConfigSchema = z.union([
  GoogleSheetsUpdateSchema,
  GoogleSheetsCreateSchema,
  GoogleDriveCSVUploadSchema,
]);
export type GoogleDriveConfig = z.infer<typeof GoogleDriveConfigSchema>;

const ExportRequestSchema = z.object({
  exportConfig: ExportConfigWithoutOrgIdSchema,
  exportName: z.string().optional(),
  email: z.string(),
  sftpConfig: SFTPConfigSchema.optional().nullable(),
  googleSheetsConfig: GoogleDriveConfigSchema.optional(),
});
export type ExportRequest = z.infer<typeof ExportRequestSchema>;

export const ExportResponseSchema = z.object({
  status: z.string(),
  url: z.string().optional(),
});
export type ExportResponse = z.infer<typeof ExportResponseSchema>;

const _ScheduleRequestSchema = ExportRequestSchema.extend({
  scheduleConfig: ScheduleConfigSchema,
});
export type ScheduleRequest = z.infer<typeof _ScheduleRequestSchema>;

export const SFTPConfigResponseSchema = z.object({
  host: z.string(),
  port: z.number(),
  username: z.string(),
  authMethod: z.nativeEnum(SFTPAuthMethod),
  path: z.string(),
});

export const ScheduleResponseSchema = z.object({
  pk: z.string(),
  entityType: z.string(),
  exportTime: ScheduleConfigSchema,
  exportConfig: ExportConfigSchema,
  exportName: z.string(),
  sftpConfig: SFTPConfigResponseSchema.optional(),
  googleSheetsConfig: GoogleDriveConfigSchema.optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  status: z.string().optional(),
  url: z.string().optional(),
});
export type ScheduleResponse = z.infer<typeof ScheduleResponseSchema>;

export const ListSchedulesResponseSchema = z.array(ScheduleResponseSchema);
export type ListSchedulesResponse = z.infer<typeof ListSchedulesResponseSchema>;
